import { useQuery } from '@tanstack/react-query';

import { normalizeChainNameForLookup } from '~/components/Offers/Detail/OfferBuilder/CMChecklist/shared/comparisons';

import OffersService from '~/services/OffersService';

import ApiError from '~/types/ApiError';

import { offerQueryKeys } from './useGetOffer';

export interface ContractTemplate {
  id: string;
  name: string;
  chain__c: string;
  geo__c?: string | null;
  ownership__c?: string | null;
  product_segment__c?: string | null;
}

// Only the Legal-owned master doc matters here -- Finance-owned payment-term templates are
// allowed to deviate per negotiated deal terms (see setupRules.ts), so never fetched.
const CONTRACT_TEMPLATE_OWNERSHIP = 'Legal';
const CONTRACT_TEMPLATE_PRODUCT_SEGMENT = 'Escapes';
const STANDARD_TEMPLATE_CHAIN = 'Standard Template';

async function findContractTemplate(
  chain: string,
  geo: string,
  options: { signal?: AbortSignal },
): Promise<ContractTemplate | null> {
  try {
    const response = await OffersService.getContractTemplate(
      { chain, geo, ownership: CONTRACT_TEMPLATE_OWNERSHIP, product: CONTRACT_TEMPLATE_PRODUCT_SEGMENT },
      options,
    );
    return response.result as ContractTemplate;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

// Raw chain -> normalized chain (comparisons.ts explains why raw goes first) -> Standard
// Template (independent hotels, or chains without a negotiated agreement, per CM process).
export function useGetContractTemplate(
  { chain, geo }: { chain?: string | null; geo?: string | null },
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery<ContractTemplate | null>({
    queryKey: [...offerQueryKeys.all, 'contract-templates', chain ?? '', geo ?? ''],
    queryFn: async ({ signal }) => {
      if (!geo) {
        return null;
      }

      if (chain) {
        const direct = await findContractTemplate(chain, geo, { signal });
        if (direct) {
          return direct;
        }

        const normalizedChain = normalizeChainNameForLookup(chain);
        if (normalizedChain && normalizedChain !== chain) {
          const normalized = await findContractTemplate(normalizedChain, geo, { signal });
          if (normalized) {
            return normalized;
          }
        }
      }

      return findContractTemplate(STANDARD_TEMPLATE_CHAIN, geo, { signal });
    },
    enabled: enabled && !!geo,
  });
}
